import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Divider,
  Chip
} from '@mui/material';
import { Edit as EditIcon, Lock as LockIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import userManagementService from '../../services/userManagementService';
import { useSelector } from 'react-redux';

const UserProfile = () => {
  const { user: authUser } = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [profileForm, setProfileForm] = useState({
    name: '',
    avatarUrl: ''
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const data = await userManagementService.getCurrentUserProfile();
      setProfile(data);
      setProfileForm({ name: data.name, avatarUrl: data.avatarUrl || '' });
    } catch (error) {
      toast.error(t('common.loadError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      await userManagementService.updateCurrentUserProfile(profileForm);
      toast.success(t('users_ext.profileUpdated'));
      fetchProfile();
      setEditDialogOpen(false);
    } catch (error) {
      toast.error(t('common.saveError'));
      console.error(error);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error(t('users.passwordMismatch'));
      return;
    }
    try {
      await userManagementService.changePassword(passwordForm);
      toast.success(t('users_ext.passwordChanged'));
      setPasswordDialogOpen(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
      console.error(error);
    }
  };

  if (loading) {
    return <Box sx={{ p: 3 }}>{t('common.loading')}</Box>;
  }

  if (!profile) {
    return <Box sx={{ p: 3 }}>{t('common.notFound')}</Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t('users.myProfile')}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{ width: 120, height: 120, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: '3rem' }}
              >
                {profile.name?.charAt(0).toUpperCase()}
              </Avatar>
              <Typography variant="h5">{profile.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {profile.email}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={profile.globalRole}
                  color={profile.globalRole === 'SUPER_ADMIN' ? 'error' : 'primary'}
                  size="small"
                />
              </Box>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditDialogOpen(true)}
                fullWidth
                sx={{ mb: 1 }}
              >
                {t('users_ext.editProfile')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={() => setPasswordDialogOpen(true)}
                fullWidth
              >
                {t('users.changePassword')}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('users_ext.accountInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.email')}
                  </Typography>
                  <Typography variant="body1">{profile.email}</Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.name')}
                  </Typography>
                  <Typography variant="body1">{profile.name}</Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.status')}
                  </Typography>
                  <Typography variant="body1">
                    <Chip
                      label={profile.isActive ? t('common.active') : t('common.inactive')}
                      color={profile.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.lastLogin')}
                  </Typography>
                  <Typography variant="body1">
                    {profile.lastLogin ? new Date(profile.lastLogin).toLocaleString() : t('common.never')}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('users_ext.assignedRole')}
                  </Typography>
                  <Typography variant="body1">
                    {profile.roleDisplayName || t('users_ext.noRoleAssigned')}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                {t('users_ext.permissions')} ({profile.rolePermissions?.length || 0})
              </Typography>
              {profile.rolePermissions && profile.rolePermissions.length > 0 ? (
                <Box>
                  {profile.rolePermissions.map((permission) => (
                    <Chip
                      key={permission.id}
                      label={permission.displayName}
                      size="small"
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('users_ext.noRolePermissions')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Profile Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>{t('users_ext.editProfile')}</DialogTitle>
        <DialogContent sx={{ minWidth: 400, pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('common.name')}
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('users_ext.avatarUrl')}
                value={profileForm.avatarUrl}
                onChange={(e) => setProfileForm({ ...profileForm, avatarUrl: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleUpdateProfile} variant="contained">
            {t('common.saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
        <DialogTitle>{t('users.changePassword')}</DialogTitle>
        <DialogContent sx={{ minWidth: 400, pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label={t('users.currentPassword')}
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label={t('users.newPassword')}
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label={t('users.confirmPassword')}
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleChangePassword} variant="contained">
            {t('users.changePassword')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserProfile;
